import { type ReactNode, useCallback, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import type { ActionMenuItem } from '@rover/components/src/callouts/ActionMenu';
import ActionMenu from '@rover/components/src/callouts/ActionMenu';
import PlayGlyph from '@rover/icons/svg/play-glyph.svg';
import { Box, Flex } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import DSTokenMap from '@rover/kibble/styles/DSTokenMap';
import MediaQuery from '@rover/kibble/styles/MediaQuery';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';

import AddImagesTile from './AddImagesTile';
import {
  GALLERY_CONTAINER_WIDTH,
  GALLERY_GRID_GAP,
  GALLERY_MIN_TILE_SIZE,
  PLAY_BADGE_OPACITY,
  PLAY_BADGE_SIZE,
} from './constants';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import { countGalleryVideoItems, galleryItemKey, isGalleryVideoItem } from './ImageGallery.common';
import ImageWithShimmer from './ImageWithShimmer';
import { getShimmerCount, shouldLoadMore } from './utils';

type ImageGridContainerProps = {
  images: GalleryItem[];
  onImageClick: (index: number) => void;
  // Mobile only props
  // eslint-disable-next-line react/no-unused-prop-types
  onClose?: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  virtualizedGrid?: boolean;
  pagination?: PaginationConfig;
  renderImageOverlay?: (index: number) => ReactNode;
  onImagesAdded?: (files: File[]) => void;
  isUploading?: boolean;
  renderImageActions?: (index: number) => ActionMenuItem[];
  columns?: number;
};

const PLAY_BADGE_SIZE_PX = `${PLAY_BADGE_SIZE}px`;

const ACTIONS_TESTID = 'gallery-image-actions';
const ACTIONS_SELECTOR = `[data-testid="${ACTIONS_TESTID}"]`;

const PLAY_BADGE_BACKGROUND = DSTokenMap.TEXT_COLOR_PRIMARY.withOpacity(PLAY_BADGE_OPACITY);
const PLAY_BADGE_FILL = `${DSTokenMap.TEXT_COLOR_PRIMARY_INVERSE}`;

const ImageGridContainer = ({
  images,
  onImageClick,
  pagination,
  renderImageOverlay,
  onImagesAdded,
  isUploading,
  renderImageActions,
  columns,
}: ImageGridContainerProps): JSX.Element | null => {
  const { t } = useLingui();
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const handleLoadMore = useCallback(() => {
    if (shouldLoadMore(pagination)) {
      pagination?.onLoadMore();
    }
  }, [pagination]);

  const sentinelRef = useOnIntersect(handleLoadMore, false);

  const loadedImageCount = images.length - countGalleryVideoItems(images);
  const shimmerCount = getShimmerCount(pagination, loadedImageCount);

  return (
    <>
      <Box
        data-testid="gallery-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${GALLERY_MIN_TILE_SIZE}, 1fr))`,
          ...(columns
            ? {
                [MediaQuery.SM_UP.toString()]: {
                  gridTemplateColumns: `repeat(${columns}, 1fr)`,
                },
              }
            : {}),
          gap: GALLERY_GRID_GAP,
          margin: '0 auto',
          justifyContent: 'center',
          width: '100%',
          maxWidth: GALLERY_CONTAINER_WIDTH,
        }}
      >
        {onImagesAdded && <AddImagesTile onImagesAdded={onImagesAdded} isUploading={isUploading} />}
        {images.map((image, index) => {
          const isVideo = isGalleryVideoItem(image);
          const photoNumber = index + 1;
          const overlay = renderImageOverlay?.(index);
          const actions = renderImageActions?.(index) ?? [];
          const overlayId = `gallery-image-overlay-${galleryItemKey(image)}`;

          return (
            <Box
              key={galleryItemKey(image)}
              position="relative"
              width="100%"
              sx={{
                '@media (hover: hover)': {
                  [`& ${ACTIONS_SELECTOR}`]: { opacity: openMenuIndex === index ? 1 : 0 },
                  [`&:hover ${ACTIONS_SELECTOR}, & ${ACTIONS_SELECTOR}:focus-within`]: {
                    opacity: 1,
                  },
                },
              }}
            >
              <ImageWithShimmer
                fluid
                src={image.small}
                caption={
                  isVideo ? image.video.description : image.caption || t`View photo ${photoNumber}`
                }
                altText={isVideo ? undefined : image.altText}
                onClick={() => onImageClick(index)}
                describedById={overlay ? overlayId : undefined}
              />
              {/* The grid shows the video's thumbnail with a play badge, never a
                  player. Opening the tile is what starts playback, in the lightbox. */}
              {isVideo && (
                <Flex
                  position="absolute"
                  top="0"
                  width="100%"
                  height="100%"
                  justifyContent="center"
                  alignItems="center"
                  data-testid="gallery-play-icon"
                  // The badge sits over the tile's own click target, so it has to
                  // let presses through rather than swallow them.
                  sx={{ pointerEvents: 'none' }}
                >
                  <Flex
                    width={PLAY_BADGE_SIZE_PX}
                    height={PLAY_BADGE_SIZE_PX}
                    borderRadius="round"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ backgroundColor: PLAY_BADGE_BACKGROUND }}
                  >
                    <PlayGlyph
                      height={PLAY_BADGE_SIZE_PX}
                      width={PLAY_BADGE_SIZE_PX}
                      fill={PLAY_BADGE_FILL}
                    />
                  </Flex>
                </Flex>
              )}
              {actions.length > 0 && (
                <Box
                  position="absolute"
                  top="2x"
                  right="2x"
                  zIndex="1"
                  data-testid={ACTIONS_TESTID}
                >
                  <ActionMenu
                    items={actions}
                    triggerLabel={t`Photo ${photoNumber} actions`}
                    onOpenChange={(isOpen) => setOpenMenuIndex(isOpen ? index : null)}
                  />
                </Box>
              )}
              {overlay && (
                <Flex
                  id={overlayId}
                  position="absolute"
                  bottom="0"
                  left="0"
                  maxWidth="100%"
                  padding="2x"
                  data-testid="gallery-image-overlay"
                  sx={{ pointerEvents: 'none' }}
                >
                  {overlay}
                </Flex>
              )}
            </Box>
          );
        })}
        {pagination?.isLoadingMore &&
          Array.from({ length: shimmerCount }, (_, i) => (
            <Box key={`shimmer-${i}`} width="100%" sx={{ aspectRatio: '1' }}>
              <LoadingShimmer
                width="100%"
                height="100%"
                borderRadius="primary"
                m="0x"
                data-testid="loading-more-shimmer"
              />
            </Box>
          ))}
      </Box>
      {pagination?.hasMore && (
        <Box ref={sentinelRef} height="1px" aria-hidden="true" data-testid="scroll-sentinel" />
      )}
    </>
  );
};

export default ImageGridContainer;
